import { FetchError, getErrorMessage, getFetchErrorUserMessage, type ErrorResponse } from '@talos/kyoko';
import { isRestEndpointRejection } from './isRestEndpointRejection';

/** Talos REST / RTK {@link ErrorResponse} built from a plain user-facing message (matches {@link restEndpointBaseQuery} failures). */
export function toRestEndpointErrorResponse(message: string): ErrorResponse {
  return {
    type: 'error',
    ts: new Date().toISOString(),
    message,
  };
}

/**
 * Maps values thrown from kyoko HTTP helpers (`Get`, `Post`, …) into the same {@link ErrorResponse}
 * shape returned by {@link restEndpointBaseQuery} on failure.
 */
export function httpClientThrownToRestEndpointErrorResponse(error: unknown): ErrorResponse {
  if (error instanceof FetchError) {
    return toRestEndpointErrorResponse(getFetchErrorUserMessage(error));
  }
  if (error instanceof Error) {
    return toRestEndpointErrorResponse(error.message || 'Request failed');
  }
  return toRestEndpointErrorResponse('Unknown error occurred');
}

/**
 * User-facing detail for REST / RTK failures: imperative `catch`, `.unwrap()`, or query `error`
 * when mixing kyoko `request()`, {@link restEndpointBaseQuery}, and RTK rejections. Returns
 * `undefined` when nothing useful can be extracted (caller may show a generic label only).
 */
export function getRestFailureUserMessage(error: unknown): string | undefined {
  if (error instanceof FetchError) {
    return getFetchErrorUserMessage(error);
  }
  if (isRestEndpointRejection(error)) {
    return getErrorMessage(error.error);
  }
  const fromShape = getErrorMessage(error as ErrorResponse | undefined);
  if (fromShape) {
    return fromShape;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return undefined;
}
